<template>
  <v-container
    id="app-home"
    class="fill-height"
  >
    <div class="bloc px-5">
      <div class="headline text-center mt-50 mb-8">
        {{ $t('info.payment_preparation') }}
      </div>
      <v-progress-linear
        indeterminate
        color="white"
      />
    </div>
  </v-container>
</template>

<script>
import { MetierLocalStorage } from 'modulelocalstorage'

import { ActionEnum, PaymentEnum } from '@/modules/Enums'
import { $alert, createReference, needToAskDonation, needToAskTip } from '@/modules/Utils'
import { generateCustomerReceipt, generateMerchantReceipt, Ticket } from '@/modules/Ticket'
import { silentNeptingLogin, silentNeptingLogout } from '@/modules/Banking'

export default {
  name: 'BankingPaymentView',

  props: {
    directPayment: { type: Boolean, default: false },
    goToCashless: { type: Boolean, default: false },
    reference: { type: String, default: null },
    idW2S: { type: Number, default: 0 },
    paymentType: { type: Number, default: 0 }
  },

  computed: {
    total () { return this.$store.state.amount },
    donationAmount () { return this.$store.state.donationAmount },
    tipAmount () { return this.$store.state.tipAmount },
    printCustomerTicket () { return this.$store.state.printCustomerTicket },
    printMerchantTicket () { return this.$store.state.printMerchantTicket }
  },

  mounted () {
    if (this.directPayment) {
      this.payment()
    } else {
      // Verification des campagnes de don et pourboire
      if (needToAskTip()) {
        this.$router.replace({ name: 'tips', params: { goToCashless: this.goToCashless } })
      } else if (needToAskDonation()) {
        this.$router.replace({ name: 'donation', params: { goToCashless: this.goToCashless } })
      } else {
        this.payment()
      }
    }
  },

  methods: {
    payment () {
      const amount = this.total + this.donationAmount + this.tipAmount

      const ref = this.reference ? this.reference : createReference()

      const data = this.$store.getters.useSoftPos ? {
        ...this.$store.state.neptingInfos,
        ...this.$store.getters.paymentParams
      } : this.$store.state.neptingInfos

      CissAndroid.payment(
        {
          amount: amount.toString(),
          type: PaymentEnum[this.paymentType],
          reference: ref,
          idW2S: this.idW2S,
          manageTickets: this.$store.state.manageBankingTickets,
          data: data
        },
        (response) => {
          this.paymentSuccess(ref, response.customerReceipt, response.merchantReceipt, response.signatureRequired)
        },
        async (response) => {
          try {
            const errorCode = response.message ? response.message.substring(0, 6) : null
            if (errorCode === '[NEP1]') {
              // Token Nepting null
              await silentNeptingLogin()
              this.payment()
            } else if (errorCode === '[NEP2]') {
              // Token Nepting dépassé en temps
              await silentNeptingLogout()
              await silentNeptingLogin()
              this.payment()
            } else {
              this.paymentError(response.message, response.customerReceipt, response.merchantReceipt)
            }
          } catch (ignored) {
            // En cas d'erreur de login / logout, un message est affiché et on retourne à l'accueil
            this.$router.replace('/home')
          }
        }
      )
    },
    //
    paymentSuccess (ref, customerTicket = null, merchantTicket = null, signatureRequired = false) {
      const hasDonation = this.donationAmount > 0
      const hasTip = this.tipAmount > 0

      // Création transaction de don
      if (hasDonation) {
        this.$store.commit('donationRef', createReference(1))

        Donation.saveTransaction(
          {
            amount: this.donationAmount,
            ref: this.$store.state.donationRef,
            label: this.$store.getters.donationCampaign.Associations[0].Enseigne,
            userData: {
              IDCampagne: this.$store.getters.donationCampaign.IDCampagne,
              IDAssociation: this.$store.getters.donationCampaign.Associations[0].IDAssociation
            }
          },
          () => { this.$store.dispatch('addTransaction', 'donation') },
          () => {}
        )
      }

      // Création transaction de pourboire
      if (hasTip) {
        this.$store.commit('tipRef', createReference(2))

        Tip.saveTransaction(
          {
            amount: this.tipAmount,
            ref: this.$store.state.tipRef,
            waiter: this.$store.state.waiterNumber,
            userData: this.$store.getters.tipCampaign.IDCampagne,
          },
          () => { this.$store.dispatch('addTransaction', 'pourboire') },
          () => {}
        )
      }

      let tickets = []

      // Ajout du ticket client
      if (customerTicket) {
        tickets.push(Ticket.formatBankingTicket(customerTicket))
      }
      // Ajout du ticket client de don/pourboire
      if (hasDonation || hasTip) {
        tickets.push(generateCustomerReceipt())
      }

      // Sauvegarde des tickets pour duplicata (avant celui du commerçant)
      MetierLocalStorage.setTicket(tickets)

      // Si on n'imprime pas les tickets client, on vide le tableau
      if (!this.printCustomerTicket) {
        tickets = []
      }

      // Si on imprime les tickets commerçants, on l'ajoute
      if (this.printMerchantTicket) {
        // Enregistrement du ticket commerçant
        if (merchantTicket) {
          tickets.push(Ticket.formatBankingTicket(merchantTicket, true))
        }
        // Ajout du ticket commerçant de don/pourboire
        if (hasDonation || hasTip) {
          tickets.push(generateMerchantReceipt())
        }
      }

      // Enregistrement des tickets bancaires pour impression
      localStorage.setItem('tickets', tickets.length > 0 ? JSON.stringify(tickets) : null)

      // Si une signature du ticket est requise, on affiche une alerte
      if (signatureRequired) {
        $alert('Paiement réussi', 'warning', 'DEMANDER SIGNATURE CLIENT')
      }

      if (this.goToCashless) {
        // Enregistrement des tickets bancaires pour impression
        Ticket.bankingTickets = tickets
        // Redirection vers cashless crédit après débit cb
        this.$router.replace({
          name: this.offline ? 'scan-nfc-offline' : 'scan-nfc',
          params: { action: ActionEnum.cashlessCredit, mode: '1', creditOnly: true, reference: ref }
        })
      } else if (tickets.length > 0) {
        this.$router.replace({
          name: 'ticket',
          params: { tickets: tickets, reference: ref, signature: signatureRequired }
        })
      } else {
        if (!signatureRequired) {
          $alert('Paiement réussi', 'success')
        }
        this.$router.replace('/home')
      }

      // Restart product keyboard
      this.$emit('show-products')
    },
    paymentError (error, customerTicket = null, merchantTicket = null) {
      const tickets = []
      // Ajout du ticket client
      if (customerTicket) {
        tickets.push(Ticket.formatBankingTicket(customerTicket))
      }
      // Ajout du ticket commerçant
      if (merchantTicket && this.printMerchantTicket) {
        tickets.push(Ticket.formatBankingTicket(merchantTicket, true))
      }

      $alert(error, 'error', '', -1)

      // Enregistrement des tickets bancaires pour impression
      localStorage.setItem('tickets', tickets.length > 0 ? JSON.stringify(tickets) : null)

      if (tickets.length > 0) {
        this.$router.replace({
          name: 'ticket',
          params: { tickets: tickets }
        })
      } else {
        this.$router.replace('/home')
      }

      // Restart product keyboard
      this.$emit('show-products')
    }
  }
}
</script>
